/**
 * Opt-in profiler for the playEngine edit loop.
 *
 * Answers "how much of the lag is DB persist vs iframe apply vs React render":
 * every hot path in the edit loop reports its duration under a label, and
 * `report()` prints totals plus each label's share of the measured time.
 *
 * Enable from the browser console (persists across reloads):
 *   __protoPerf.on()      // then edit something in Version Settings
 *   __protoPerf.report()
 *   __protoPerf.off()
 *
 * Disabled by default; when off, every entry point is a no-op call.
 */

const STORAGE_KEY = "playEnginePerfLog";

let enabled = false;
try {
  enabled = window.localStorage.getItem(STORAGE_KEY) === "1";
} catch (_) {
  enabled = false;
}

/** label -> { count, total, max } (durations in ms) */
const stats = new Map();
let startedAt = enabled ? performance.now() : 0;

// Long tasks (>50ms of uninterrupted main-thread work) are what actually makes
// the same-origin preview iframe stutter, so track them alongside the labels.
let longTaskTotal = 0;
let longTaskCount = 0;
let longTaskObserver = null;

function startLongTaskObserver() {
  if (longTaskObserver || typeof PerformanceObserver === "undefined") return;
  try {
    longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTaskTotal += entry.duration;
        longTaskCount += 1;
      }
    });
    longTaskObserver.observe({ entryTypes: ["longtask"] });
  } catch (_) {
    longTaskObserver = null;
  }
}

function stopLongTaskObserver() {
  if (!longTaskObserver) return;
  longTaskObserver.disconnect();
  longTaskObserver = null;
}

if (enabled) startLongTaskObserver();

function record(label, duration) {
  const entry = stats.get(label) || { count: 0, total: 0, max: 0 };
  entry.count += 1;
  entry.total += duration;
  if (duration > entry.max) entry.max = duration;
  stats.set(label, entry);
}

export function perfEnabled() {
  return enabled;
}

/**
 * Measure a synchronous block:
 *   const end = perfStart("persist:cloneDeep"); ...; end();
 * Returns a no-op when profiling is off.
 */
export function perfStart(label) {
  if (!enabled) return noop;
  const t0 = performance.now();
  return function end() {
    record(label, performance.now() - t0);
  };
}

function noop() {}

/** Measure an async block, preserving its resolved value / thrown error. */
export async function perfAsync(label, fn) {
  if (!enabled) return fn();
  const t0 = performance.now();
  try {
    return await fn();
  } finally {
    record(label, performance.now() - t0);
  }
}

export function perfReset() {
  stats.clear();
  longTaskTotal = 0;
  longTaskCount = 0;
  startedAt = performance.now();
}

export function perfReport() {
  const rows = [...stats.entries()]
    .map(([label, { count, total, max }]) => ({ label, count, total, max }))
    .sort((a, b) => b.total - a.total);
  const measured = rows.reduce((sum, r) => sum + r.total, 0) || 1;
  const wall = performance.now() - startedAt;

  // eslint-disable-next-line no-console
  console.table(
    rows.map((r) => ({
      label: r.label,
      calls: r.count,
      "total ms": Math.round(r.total),
      "share %": `${((r.total / measured) * 100).toFixed(1)}%`,
      "avg ms": (r.total / r.count).toFixed(1),
      "worst ms": Math.round(r.max),
    }))
  );
  // eslint-disable-next-line no-console
  console.log(
    `[protoPerf] window ${Math.round(wall)}ms · measured ${Math.round(measured)}ms · ` +
    `long tasks ${longTaskCount} (${Math.round(longTaskTotal)}ms blocked)`
  );
  return rows;
}

function setEnabled(next) {
  enabled = next;
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, "1");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) { /* ignore */ }
  if (next) {
    perfReset();
    startLongTaskObserver();
  } else {
    stopLongTaskObserver();
  }
}

if (typeof window !== "undefined") {
  window.__protoPerf = {
    on: () => setEnabled(true),
    off: () => setEnabled(false),
    report: perfReport,
    reset: perfReset,
    get enabled() {
      return enabled;
    },
  };
}
